import { supabase } from './supabase'
import { dequeue, enqueue, listQueue, markAttempt, type QueuedSend } from './queue'

/**
 * Sending a document, and seeing what happened to it afterwards.
 *
 * The upload is deliberately NOT awaited by the UI. The sender sees "Sent ✓"
 * the moment the capture is queued; everything below happens behind them. That
 * is rule 20 and 21 in CLAUDE.md, and it is the difference between beating
 * WhatsApp and losing to it.
 */

/**
 * The six states of spec §6's closure table, plus the two that only exist on
 * the phone (`sending` from the offline queue, `failed` when the pipeline gave
 * up). The order below is the order they happen in, and `my_sends` returns the
 * furthest one reached — a document never travels backwards.
 */
export type SendState =
  | 'sending'
  | 'sent'
  | 'seen'
  | 'handling'
  | 'paid'
  | 'handled'
  | 'stored'
  | 'failed'

export interface SentItem {
  id: string
  createdAt: string
  state: SendState
  /** What the admin has done with it, in the parent's language. */
  statusLine: string
  /** The supplier, or the letter's subject. Null until anyone knows. */
  title: string | null
  /** Set on `paid`: what was actually paid, from the ledger. */
  amountCents: number | null
  /** The day it was settled or ticked off. */
  closedOn: string | null
  pending?: boolean
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

/** Queue a send. Returns immediately; the flush runs in the background. */
export async function queueSend(params: {
  spaceId: string
  uploadedBy: string
  pages: Blob[]
  /**
   * 'scan' is a parent at the shutter; 'file' is a PDF or a photo chosen from
   * the phone. Worth distinguishing because a PDF invoice reads perfectly and
   * a photograph of one does not, and the trail should say which this was.
   */
  source?: 'scan' | 'file'
  /**
   * Supply the key when something else needs to refer to this upload before it
   * exists. An expense does: the amount is saved immediately, the photo goes
   * through the offline queue, and the key is the only name they can both use
   * for a document that has not been created yet. See 0032.
   */
  idempotencyKey?: string
}): Promise<string> {
  const item: QueuedSend = {
    id: crypto.randomUUID(),
    spaceId: params.spaceId,
    uploadedBy: params.uploadedBy,
    idempotencyKey: params.idempotencyKey ?? newIdempotencyKey(),
    pages: params.pages,
    source: params.source ?? 'scan',
    createdAt: Date.now(),
    attempts: 0,
  }
  await enqueue(item)
  void flushQueue()
  return item.id
}

function extensionFor(contentType: string): string {
  if (contentType === 'application/pdf') return 'pdf'
  if (contentType === 'image/png') return 'png'
  if (contentType === 'image/heic') return 'heic'
  if (contentType.startsWith('audio/mp4')) return 'm4a'
  if (contentType.startsWith('audio/webm')) return 'webm'
  return 'jpg'
}

let flushing = false

/** Upload everything waiting. Safe to call often; it will not overlap itself. */
export async function flushQueue(): Promise<void> {
  if (flushing || !navigator.onLine) return
  flushing = true
  try {
    for (const item of await listQueue()) {
      try {
        await uploadOne(item)
        await dequeue(item.id)
      } catch (err) {
        // Stay in the queue and try again next time the app is opened.
        await markAttempt(item.id, String(err))
      }
    }
  } finally {
    flushing = false
  }
}

async function uploadOne(item: QueuedSend): Promise<void> {
  // The document row first, so the idempotency key claims its slot before any
  // bytes move. A retry hits the unique constraint and is treated as done.
  const { data: doc, error: docError } = await supabase
    .from('documents')
    .insert({
      space_id: item.spaceId,
      idempotency_key: item.idempotencyKey,
      uploaded_by: item.uploadedBy,
      // Items queued before this field existed default to 'scan', which is
      // what they were.
      source: item.source ?? 'scan',
      state: 'pending',
      // A voice note is carried in the same queue but is not a page, and
      // page_count is what the app shows as "3 pagina's".
      page_count: item.pages.filter((p) => !p.type.startsWith('audio/')).length,
    })
    .select('id')
    .single()

  let documentId = doc?.id as string | undefined

  if (docError) {
    if (docError.code === '23505') {
      // Already created by an earlier attempt — find it and carry on.
      const { data: existing } = await supabase
        .from('documents')
        .select('id')
        .eq('idempotency_key', item.idempotencyKey)
        .maybeSingle()
      documentId = existing?.id as string | undefined
      if (!documentId) throw docError
    } else {
      throw docError
    }
  }
  if (!documentId) throw new Error('No document id')

  for (let i = 0; i < item.pages.length; i++) {
    const page = item.pages[i]!
    // The blob carries its own type through IndexedDB, so a PDF stays a PDF
    // all the way to Azure — which reads its text layer and gets the amounts
    // right in a way no photograph ever will.
    const contentType = page.type || 'image/jpeg'
    const key = `${item.spaceId}/${documentId}/${i + 1}.${extensionFor(contentType)}`

    const { error: upError } = await supabase.storage
      .from('documents')
      .upload(key, page, { contentType, upsert: true })
    if (upError) throw upError

    const { error: attError } = await supabase.from('attachments').insert({
      space_id: item.spaceId,
      document_id: documentId,
      storage_key: key,
      content_type: contentType,
      byte_size: page.size,
      page_number: i + 1,
    })
    // A duplicate attachment from a retry is harmless; anything else is not.
    if (attError && attError.code !== '23505') throw attError
  }

  await supabase.from('activity_log').insert({
    space_id: item.spaceId,
    actor_id: item.uploadedBy,
    action: 'document.sent',
    entity_type: 'document',
    entity_id: documentId,
    detail: { pages: item.pages.length },
  })
}

/** Undo within the 60-second window. Enforced server-side, not here. */
export async function undoSend(documentId: string): Promise<void> {
  const { error } = await supabase.rpc('undo_send', { p_document_id: documentId })
  if (error) throw error
}

/**
 * The parent's "recently sent" list — the closure trail, and the single
 * biggest reason to use this instead of WhatsApp.
 */
export async function listMySends(limit = 20): Promise<SentItem[]> {
  // One RPC rather than a select on `documents`.
  //
  // The select had two faults. It filtered on nothing but RLS, and migration
  // 0029 widened RLS so that a member can read every document in a *shared*
  // space — so "Recent verstuurd" quietly began listing things the other
  // parent had sent, as if you had sent them yourself.
  //
  // And it could only ever reach 'Ontvangen ✓', because whether a bill was
  // actually paid lives in obligations and payment_events, which a member
  // cannot read and should not be able to. `my_sends` answers the question
  // without opening those tables: see migration 0043.
  const { data, error } = await supabase.rpc('my_sends', { p_limit: limit })
  if (error) throw error

  const queued = await listQueue()

  const pending: SentItem[] = queued.map((q) => ({
    id: q.id,
    createdAt: new Date(q.createdAt).toISOString(),
    state: 'sending',
    statusLine: q.attempts > 2 ? 'Wordt nog verstuurd…' : 'Versturen…',
    title: null,
    amountCents: null,
    closedOn: null,
    pending: true,
  }))

  const uploaded: SentItem[] = (data ?? []).map((row: Record<string, unknown>) => {
    const state = (row.closure as SendState) ?? 'sent'
    const amountCents = row.amount_cents === null ? null : Number(row.amount_cents)
    return {
      id: row.document_id as string,
      createdAt: row.uploaded_at as string,
      state,
      title: (row.title as string | null) ?? null,
      amountCents,
      closedOn: (row.closed_on as string | null) ?? null,
      statusLine: closureLine(state, {
        handler: (row.handler as string | null) ?? null,
        amountCents,
        closedOn: (row.closed_on as string | null) ?? null,
      }),
    }
  })

  return [...pending, ...uploaded]
}

/**
 * The sentence the parent reads. Spec §6's table, in Dutch.
 *
 * Two of these matter more than the rest. "Betaald ✓ · €840 · 22 aug" is the
 * one the spec calls the reason they keep sending things instead of drifting
 * back to WhatsApp — so it carries the amount and the day, not just a tick.
 *
 * And "Niets nodig — bewaard" is for the junk mail. A parent who is made to
 * feel they wasted your time starts filtering what they send, and the one that
 * gets filtered out will eventually be the one that mattered. Telling them it
 * was kept is the whole point of the line.
 */
export function closureLine(
  state: SendState,
  ctx: { handler?: string | null; amountCents?: number | null; closedOn?: string | null } = {},
): string {
  const who = ctx.handler?.split(' ')[0]

  switch (state) {
    case 'sending':
      return 'Versturen…'
    case 'paid': {
      const parts = ['Betaald ✓']
      if (ctx.amountCents != null) parts.push(formatEuro(ctx.amountCents))
      if (ctx.closedOn) parts.push(shortDay(ctx.closedOn))
      return parts.join(' · ')
    }
    case 'handled':
      return who ? `${who} heeft het afgehandeld ✓` : 'Afgehandeld ✓'
    case 'handling':
      return who ? `${who} is ermee bezig` : 'Wordt behandeld'
    case 'seen':
      return who ? `${who} heeft het gezien` : 'Aangekomen ✓'
    case 'stored':
      // Deliberately not "niets aan de hand": that reads as "you sent nothing
      // useful". This says the send was worth making and the paper is kept.
      return 'Niets nodig — bewaard'
    case 'failed':
      // Rule 21: capture never fails visibly. From the parent's side a
      // document the pipeline could not read is simply one a human will look
      // at, which is also true.
      return 'Wordt nagekeken'
    default:
      return 'Verstuurd ✓'
  }
}

function formatEuro(cents: number): string {
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100)
}

function shortDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
  return new Intl.DateTimeFormat('nl-BE', { day: 'numeric', month: 'short' }).format(
    new Date(y, m - 1, d),
  )
}

